
/**
 * The cart. Totals are always the server's answer, never arithmetic done here.
 */

import { Trash } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { useCart } from '@/components/cart-provider';
import { GarmentMockup } from '@/components/garment-mockup';
import { ButtonLink, Callout, EmptyState } from '@/components/ui';
import { formatCents } from '@/lib/money';
import type { GarmentType } from '@/lib/types';

export interface PricedCartLine {
  published_id: string;
  title: string;
  type: GarmentType;
  color_name: string;
  color_hex: string;
  design_url: string;
  pos_x: number | null;
  pos_y: number | null;
  scale: number | null;
  size: string;
  qty: number;
  unit_price_cents: number;
  line_total_cents: number;
}

export interface PricedCartResponse {
  lines: PricedCartLine[];
  subtotal_cents: number;
  shipping_cents: number;
  gst_cents: number;
  total_cents: number;
}

export function useCartPricing() {
  const { lines, ready } = useCart();
  const [priced, setPriced] = useState<PricedCartResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reprice = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/cart-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body?.error?.message ?? 'We could not price this cart.');
        setPriced(null);
        return;
      }
      setError(null);
      setPriced(body as PricedCartResponse);
    } catch {
      setError('We could not reach the server to price this cart.');
    } finally {
      setLoading(false);
    }
  }, [lines]);

  useEffect(() => {
    if (!ready) return;
    void reprice();
  }, [ready, reprice]);

  return { priced, error, loading, ready };
}

export function CartView() {
  const { setQty, remove } = useCart();
  const { priced, error, loading, ready } = useCartPricing();

  if (!ready || loading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-[10px] bg-panel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <Callout tone="danger" title={error}>
          One of the items may have sold out or been unpublished. Remove it and try again.
        </Callout>
        <ButtonLink href="/shop" weight="secondary">
          Browse the store
        </ButtonLink>
      </div>
    );
  }

  if (!priced || priced.lines.length === 0) {
    return (
      <EmptyState
        title="Your cart is empty"
        body="Add something from the store, or start a custom order if you need a run printed."
        action={<ButtonLink href="/shop">Browse the store</ButtonLink>}
      />
    );
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
      <ul className="space-y-4">
        {priced.lines.map((line) => (
          <li
            key={`${line.published_id}-${line.size}`}
            className="flex gap-4 rounded-[10px] border border-line bg-panel p-4"
          >
            <div className="w-24 shrink-0 rounded-[10px] bg-raised p-1.5">
              <GarmentMockup
                type={line.type}
                colorHex={line.color_hex}
                designUrl={line.design_url}
                placement={{
                  pos_x: line.pos_x ?? 50,
                  pos_y: line.pos_y ?? 48,
                  scale: line.scale ?? 60,
                }}
                label={`${line.title} in ${line.color_name}`}
                className="aspect-square w-full"
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <Link
                to="/product/$id" params={{ id: line.published_id }}
                className="text-[15px] font-bold leading-snug hover:text-brand-ink"
              >
                {line.title}
              </Link>
              <p className="mt-0.5 text-sm text-muted">
                {line.color_name} &middot; Size {line.size}
              </p>
              <p className="mt-0.5 text-sm text-muted">
                {formatCents(line.unit_price_cents)} each
              </p>

              <div className="mt-auto flex flex-wrap items-center gap-3 pt-3">
                <div className="inline-flex items-center rounded-[10px] border border-line-strong">
                  <button
                    type="button"
                    onClick={() => setQty(line.published_id, line.size, line.qty - 1)}
                    className="grid size-11 place-items-center text-lg font-bold hover:bg-raised"
                    aria-label={`Decrease quantity of ${line.title}`}
                  >
                    &minus;
                  </button>
                  <span className="w-9 text-center font-mono text-[15px] font-bold">
                    {line.qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQty(line.published_id, line.size, line.qty + 1)}
                    className="grid size-11 place-items-center text-lg font-bold hover:bg-raised"
                    aria-label={`Increase quantity of ${line.title}`}
                  >
                    +
                  </button>
                </div>

                {/* Tertiary weight: removing a line is the least important action
                    in this row, so it should not compete with the stepper. */}
                <button
                  type="button"
                  onClick={() => remove(line.published_id, line.size)}
                  className="inline-flex items-center gap-1.5 py-2 text-sm font-semibold text-muted underline underline-offset-4 hover:text-ink"
                >
                  <Trash size={15} weight="bold" aria-hidden />
                  Remove
                </button>
              </div>
            </div>

            <p className="shrink-0 text-[15px] font-bold">
              {formatCents(line.line_total_cents)}
            </p>
          </li>
        ))}
      </ul>

      <aside className="rounded-[10px] border border-line bg-panel p-5 lg:sticky lg:top-24">
        <h2 className="text-sm font-bold">Order summary</h2>
        <dl className="mt-4 space-y-2.5 text-[15px]">
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Subtotal</dt>
            <dd className="font-semibold">{formatCents(priced.subtotal_cents)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Shipping</dt>
            <dd className="font-semibold">
              {priced.shipping_cents === 0 ? 'Free' : formatCents(priced.shipping_cents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-line pt-3 text-lg">
            <dt className="font-bold">Total</dt>
            <dd className="font-extrabold">{formatCents(priced.total_cents)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[13px] text-muted">GST included</dt>
            <dd className="text-[13px] text-muted">{formatCents(priced.gst_cents)}</dd>
          </div>
        </dl>

        <ButtonLink href="/checkout" size="lg" className="mt-6 w-full">
          Checkout
        </ButtonLink>
      </aside>
    </div>
  );
}
